import { useSubscription } from "@apollo/client";
import { Popover, Table } from "antd";
import { get } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsViewList } from "react-icons/bs";
import { IoDuplicateOutline } from "react-icons/io5";
import {
  MdAssignmentInd,
  MdDeleteOutline,
  MdOpenInNew,
  MdOutlineEventBusy,
  MdOutlineLock,
  MdOutlineLockOpen,
} from "react-icons/md";
import Avatar from "shared/components/Avatar";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Pagination from "shared/components/Pagination";
import Select from "shared/components/Select";

import Status from "shared/components/Status";
import { useLazyQuery, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import graphql from "utils/api/graphql";
import { formatParisDateTime } from "utils/helpers/date";
import { QUALIFICATIONS } from "utils/api/graphql/queries/qualifications";
import { USERS } from "utils/api/graphql/queries/users";
import { isManageableProjectInOrganizationScope } from "utils/helpers/project";
import { resolveProjectStatus } from "utils/helpers/projectStatus";
import {
  ACTIONS,
  PROCESS_TAGS as PROCESS_TAGS_STATUSES,
  PROJECTS_SCOPE,
  QUALIFIER_TAGS,
  SUBSCRIPTION_STATUSES,
  THEMES_PALETTE,
  USER_TYPES,
} from "utils/constants";
import { shouldShowResumeSubscription } from "./shouldShowResumeSubscription";
import classNames from "classnames";

const SIGNED_LIKE_PROCESS_TAGS = [
  PROCESS_TAGS_STATUSES.SIGNED,
  PROCESS_TAGS_STATUSES.SIGNED_INCOMPLETE,
  PROCESS_TAGS_STATUSES.SENT_TO_MANAGEMENT,
];

const LOCK_ICON = {
  [true]: <MdOutlineLock size={24} color={COLORS.C_GRAY} />,
  [false]: <MdOutlineLockOpen size={24} color={COLORS.C_GRAY} />,
};

const ProjectsTable = ({
  tracers,
  count,
  data,
  setValues,
  type,
  skip,
  onDuplicate,
  onDelete,
  onDetail,
  onAssign,
  isAssigning,
  refetch: refetchProjects,
  showDrawerSubscription,
  showDrawerSign,
  setContactId,
  setProject,
  setOpen,
  onChangeStatus,
  actions,
  pagination,
  matchStatusesWithProcess,
  user,
  scope,
  showScopeToggle = false,
  paginationFootprint = "projectsList",
}) => {
  const isOrganizationScope =
    showScopeToggle && scope === PROJECTS_SCOPE.ORGANIZATION;
  const isAdmin = get(user, "meta.type") === USER_TYPES.INSURANCE_ADMIN.type;
  const isManageableProject = (project) => {
    if (!isOrganizationScope) return true;
    return isManageableProjectInOrganizationScope(project, user);
  };
  // The admin pilots the whole organization, so he reassigns and follows up on
  // every project, but he only opens the ones he authored, i.e. his duplicates.
  const canOpenProjectDetail = (project) =>
    !isAdmin || get(project, "author.id") === get(user, "id");
  const allFieldsNull = useMemo(
    () =>
      data.every(
        (item) =>
          !get(
            get(THEMES_PALETTE, get(item, "fields.processTemplate", "DEFAULT")),
            "NAME",
          ),
      ),
    [],
  );
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const FILTER = t("FILTER", { returnObjects: true });
  const LIST = t("LIST", { returnObjects: true });
  const PROCESS_TAGS = t("PROCESS_TAGS", { returnObjects: true });
  const viewContactCard = (contactId, project) => {
    setContactId(contactId);
    setProject(project);
    setOpen(true);
  };
  const [qualifiers, setQualifiers] = useState({});
  const [isReady, setIsReady] = useState(false);
  const [advisorOverrides, setAdvisorOverrides] = useState({});

  const contactIds = useMemo(() => {
    if (!data) return [];
    return data.map(({ contact }) => contact?.id).filter(Boolean);
  }, [data]);
  const { refetch } = useQuery(QUALIFICATIONS, {
    variables: {
      withDeleted: true,
      where: {
        AND: [
          { last: true },
          { OR: contactIds.map((id) => ({ contact: { id } })) },
        ],
      },
    },
    onCompleted: ({ qualifications }) => {
      const newQualifiers = {};
      qualifications?.data.forEach(({ qualifier, contact }) => {
        newQualifiers[contact?.id] = qualifier?.name;
      });

      setQualifiers(newQualifiers);
    },
  });

  const [refetchUsers] = useLazyQuery(USERS, {
    variables: {
      where: {
        isActive: true,
        OR: [
          ...(isAdmin
            ? [{ meta: { type: USER_TYPES.INSURANCE_ADMIN.type } }]
            : []),
          { meta: { type: USER_TYPES.INTERNAL_ADVISOR.type } },
          { meta: { type: USER_TYPES.INTERNAL_SUPERVISOR.type } },
          { meta: { type: USER_TYPES.BROKER_EMPLOYEE.type } },
          { meta: { type: USER_TYPES.BROKER_ADMIN.type } },
          { meta: { type: USER_TYPES.BROKER_REPRESENTATIVE.type } },
        ],
      },
    },
    fetchPolicy: "network-only",
  });

  useSubscription(graphql.subscriptions.QUALIFICATION_ADDED, {
    skip: !isReady || contactIds.length === 0,
    variables: {
      withDeleted: true,
      where: {
        AND: [
          { last: true },
          { OR: contactIds.map((id) => ({ contact: { id } })) },
        ],
      },
    },
    onData: ({ data: subscriptionData }) => {
      const qualification = subscriptionData?.data?.qualificationAdded;
      const contactId = qualification?.contact?.id;
      const tag = qualification?.qualifier?.tag;

      if (!contactId) return;

      setQualifiers((prev) => ({
        ...prev,
        [contactId]: qualification?.qualifier?.name,
      }));

      const autoAssign = tag === QUALIFIER_TAGS.PURSUIT_RADIANCE;

      autoAssign &&
        refetchUsers().then(({ data: userData }) => {
          const users = userData?.users?.data ?? [];

          const extDistPartnerUser = users.find(
            (u) => u?.params?.extDistPartner === true,
          );

          extDistPartnerUser &&
            setAdvisorOverrides((prev) => ({
              ...prev,
              [contactId]: extDistPartnerUser.id,
            }));

          const relatedProject = data.find(
            (project) => get(project, "contact.id") === contactId,
          );

          const assignTo =
            relatedProject &&
            relatedProject.author?.id !== extDistPartnerUser?.id;

          assignTo &&
            onAssign({
              projectId: relatedProject?.id,
              projectFields: relatedProject?.fields,
              authorId: extDistPartnerUser?.id,
            });
        });

      refetch();
    },
  });

  useEffect(() => {
    if (data) setIsReady(true);
  }, [data]);

  const renderAdvisorCell = (record, project) => {
    const statusData = matchStatusesWithProcess(get(project, "statuses"));
    const currentStatus = get(statusData, "processStatus");

    const contactId = get(project, "contact.id");
    const overrideAuthorId = advisorOverrides[contactId];

    const selectedAuthorId =
      overrideAuthorId && overrideAuthorId !== record?.id
        ? overrideAuthorId
        : record?.id;

    const hasTakenOver = get(project, "hasResponsible");
    const isDistributed = !!record && !hasTakenOver && selectedAuthorId;

    return (
      <div className="d-flex align--center gap-4">
        <div className="select_200">
          <Select
            type="async"
            value={selectedAuthorId}
            node={{
              query: USERS,
              variables: {
                take: null,
                exclude: ["telecoms", "attachments", "broker", "contact"],
                where: {
                  AND: [
                    { isActive: true },
                    {
                      OR: [
                        ...(isAdmin
                          ? [
                              {
                                meta: { type: USER_TYPES.INSURANCE_ADMIN.type },
                              },
                            ]
                          : []),
                        { meta: { type: USER_TYPES.INTERNAL_ADVISOR.type } },
                        {
                          meta: { type: USER_TYPES.INTERNAL_SUPERVISOR.type },
                        },
                        { meta: { type: USER_TYPES.BROKER_EMPLOYEE.type } },
                        { meta: { type: USER_TYPES.BROKER_ADMIN.type } },
                        {
                          meta: {
                            type: USER_TYPES.BROKER_REPRESENTATIVE.type,
                          },
                        },
                      ],
                    },
                    ...(overrideAuthorId ? [{ id: overrideAuthorId }] : []),
                  ],
                },
              },
              accessors: {
                root: "users.data",
                label: {
                  separator: " ",
                  keys: ["firstname", "lastname"],
                },
              },
            }}
            placeholder={FILTER.BROKER}
            onChange={(value) => {
              setAdvisorOverrides((prev) => ({
                ...prev,
                [contactId]: value,
              }));

              onAssign({
                projectId: project.id,
                authorId: value,
              }).then(() => refetchProjects?.());
            }}
            disabled={
              isAssigning[project?.id] ||
              SIGNED_LIKE_PROCESS_TAGS.includes(currentStatus?.tag)
            }
            loading={isAssigning[project?.id]}
          />
        </div>
        {record && hasTakenOver && (
          <span className="takeover-badge takeover-badge--active">
            {LIST.TAKEN_OVER}
          </span>
        )}
        {isDistributed && (
          <span className="takeover-badge takeover-badge--distributed">
            {LIST.DISTRIBUTED}
          </span>
        )}
        {get(project, "fields.isManualAssign") && (
          <MdAssignmentInd size={16} color={COLORS.C_PRIMARY} />
        )}
      </div>
    );
  };

  const columns = [
    {
      width: 48,
      // responsive: ["sm"],
      dataIndex: "contact",
      render: (contact, project) => {
        return (
          <>
            {get(project, "fields.weedoTrust.scoreTotal") ? (
              <span
                title={get(LIST, "WEEDO_TRUST_SCORE_TOOLTIP")}
                className={classNames("weedo-trust-score", {
                  green:
                    Number(get(project, "fields.weedoTrust.scoreTotal")) >= 90,
                  orange:
                    Number(get(project, "fields.weedoTrust.scoreTotal")) < 90,
                })}
              >
                <span>{get(project, "fields.weedoTrust.scoreTotal")}</span>
              </span>
            ) : (
              <Avatar
                mode={"text"}
                size={"medium"}
                value={`${get(contact, "user.firstname", "")?.substring(
                  0,
                  1,
                )}${get(contact, "user.lastname", "")?.substring(0, 1)}`}
              />
            )}
          </>
        );
      },
      fixed: true,
    },
    {
      title: LIST.CLIENT,
      dataIndex: "contact",
      render: (contact, project) => (
        <div className="name--column">
          <div className="d-flex">
            <div
              className="name--client"
              onClick={() => viewContactCard(get(contact, "id"), project)}
            >
              {`${get(contact, "user.firstname", "")} ${get(
                contact,
                "user.lastname",
                "",
              )}`}
            </div>
          </div>
          <span className="name--column__source">
            Source : {get(project, "tracer.name")}
          </span>
          {get(project, "fields.contactType") && (
            <span className="name--column__type">
              Type : {get(project, "fields.contactType").toLowerCase()}
            </span>
          )}
        </div>
      ),
      fixed: true,
    },
    ...(allFieldsNull
      ? []
      : [
          {
            title: LIST.OFFER,
            dataIndex: "fields",
            render: (fields) => {
              const text = get(
                get(THEMES_PALETTE, fields.processTemplate, "DEFAULT"),
                "NAME",
              );
              return text ? (
                <Status
                  status="gray"
                  style={{
                    color: get(
                      get(THEMES_PALETTE, fields.processTemplate, "DEFAULT"),
                      "COLOR",
                    ),
                    background: get(
                      get(THEMES_PALETTE, fields.processTemplate, "DEFAULT"),
                      "BACKGROUND",
                    ),
                  }}
                  text={text}
                />
              ) : null;
            },
          },
        ]),
    {
      title: LIST.STATUS,
      dataIndex: "statuses",
      render: (record, project) => {
        const statusData = matchStatusesWithProcess(record);
        const { processStatus: currentStatus } = resolveProjectStatus(
          { statuses: record },
          tracers,
        );
        const statusText =
          project.expired && currentStatus?.tag !== PROCESS_TAGS_STATUSES.CLOSE
            ? PROCESS_TAGS.EXPIRED
            : get(currentStatus, "name", PROCESS_TAGS.IN_PROGRESS);

        const statusesOptions = get(
          statusData,
          "tracer.flow.process.processStatuses",
          [],
        )
          .filter(({ tag }) =>
            [
              PROCESS_TAGS_STATUSES.SIGNED,
              PROCESS_TAGS_STATUSES.CLOSE,
            ].includes(tag),
          )
          .map((status) => {
            const tag = get(status, "tag");
            return {
              label: get(status, "name"),
              value: get(status, "id"),
              onClick: [PROCESS_TAGS_STATUSES.SIGNED].includes(status.tag)
                ? () =>
                    showDrawerSign({
                      id: project.id,
                      statuses: project.statuses,
                    })
                : () =>
                    onChangeStatus({
                      project,
                      status,
                    }),
              disabled:
                ([
                  PROCESS_TAGS_STATUSES.SIGNED,
                  PROCESS_TAGS_STATUSES.SIGNED_INCOMPLETE,
                  PROCESS_TAGS_STATUSES.SENT_TO_MANAGEMENT,
                ].includes(status.tag) &&
                  !project.locked) ||
                (tag === PROCESS_TAGS_STATUSES.SIGNED && project?.expired),
              popover:
                [
                  PROCESS_TAGS_STATUSES.SIGNED,
                  PROCESS_TAGS_STATUSES.SIGNED_INCOMPLETE,
                  PROCESS_TAGS_STATUSES.SENT_TO_MANAGEMENT,
                ].includes(status.tag) && !project.locked
                  ? LIST.STATUS_POPOVER
                  : "",
            };
          });

        return ![
          PROCESS_TAGS_STATUSES.SIGNED_INCOMPLETE,
          PROCESS_TAGS_STATUSES.SIGNED,
          PROCESS_TAGS_STATUSES.SENT_TO_MANAGEMENT,
        ].includes(get(currentStatus, "tag")) ? (
          <div className="select_200">
            <Select
              popupMatchSelectWidth={false}
              type="tag"
              value={statusText}
              options={statusesOptions}
            />
          </div>
        ) : (
          <Status
            type="custom"
            style={{
              color: get(currentStatus, "color"),
              background: get(currentStatus, "background"),
            }}
            text={statusText}
          />
        );
      },
    },
    {
      title: LIST.LAST_ACTION,
      dataIndex: "contact",
      render: (contact) => (
        <div style={{ minWidth: 150 }} className="text-sec">
          {qualifiers[contact?.id] || ""}{" "}
        </div>
      ),
    },
    {
      title: LIST.CREATION_DATE,
      dataIndex: "createdDate",
      render: (record) => formatParisDateTime(record),
    },
    {
      title: LIST.ADVISOR,
      dataIndex: "author",
      render: (record, project) => {
        if (!isManageableProject(project)) {
          return (
            <div className="text-sec">
              {`${get(record, "firstname", "")} ${get(record, "lastname", "")}`}
            </div>
          );
        }
        return renderAdvisorCell(record, project);
      },
    },
    {
      width: 62,
      dataIndex: "locked",
      align: "right",
      render: (record, row) => (
        <div className="d-flex justify--end">
          {row.expired && (
            <Popover content={LIST.EXPIRED_POPOVER}>
              <MdOutlineEventBusy size={24} color={COLORS.C_DANGER} />
            </Popover>
          )}
          {record && (
            <Popover
              content={row.expired ? LIST.EXPIRED_LOCKED : LIST.LOCKED_POPOVER}
            >
              {LOCK_ICON[record]}
            </Popover>
          )}
        </div>
      ),
    },
    ...(actions[ACTIONS.UPDATE_SUBSCRIPTION] ||
    actions[ACTIONS.DELETE_PROJECT] ||
    actions[ACTIONS.DUPLICATE_PROJECT] ||
    actions[ACTIONS.UPDATE_PROJECT]
      ? [
          {
            title: LIST.ACTIONS,
            dataIndex: "id",
            align: "right",
            render: (id, project) => {
              const statusData = matchStatusesWithProcess(
                get(project, "statuses", []),
              );
              const currentStatus = get(statusData, "processStatus");
              const disableDetail =
                currentStatus?.tag === PROCESS_TAGS_STATUSES.CLOSE;
              const isSigned = get(project, "subscriptions", [])?.some(
                (subscription) =>
                  get(subscription, "status") === SUBSCRIPTION_STATUSES.SIGNED,
              );
              const canManageProject = isManageableProject(project);
              return (
                <div className="d-flex justify--end">
                  {shouldShowResumeSubscription({
                    project,
                    canManageProject,
                    canUpdateSubscription: actions[ACTIONS.UPDATE_SUBSCRIPTION],
                  }) && (
                    <Button
                      type="primary--light"
                      icon={<BsViewList />}
                      onClick={() => showDrawerSubscription(id)}
                    >
                      {LIST.RESUME_SUBSCRIPTION}
                    </Button>
                  )}

                  {actions[ACTIONS.DUPLICATE_PROJECT] && (
                    <Popover content={LIST.DUPLICATE}>
                      <IoDuplicateOutline
                        size={24}
                        onClick={() => onDuplicate(project)}
                      />
                    </Popover>
                  )}

                  {canManageProject && actions[ACTIONS.DELETE_PROJECT] && (
                    <Popover content={LIST.DELETE}>
                      <MdDeleteOutline
                        color={
                          isSigned
                            ? COLORS.C_DANGER_BACKGROUND
                            : COLORS.C_DANGER
                        }
                        size={24}
                        onClick={() => (isSigned ? null : onDelete(id))}
                      />
                    </Popover>
                  )}
                  {canManageProject &&
                    canOpenProjectDetail(project) &&
                    actions[ACTIONS.PROJECT] &&
                    actions[ACTIONS.UPDATE_PROJECT] && (
                      <Popover content={LIST.ACCESS}>
                        <MdOpenInNew
                          color={
                            disableDetail
                              ? COLORS.C_PRIMARY_BACKGROUND
                              : COLORS.C_PRIMARY
                          }
                          size={24}
                          onClick={() =>
                            disableDetail ? null : onDetail(project)
                          }
                        />
                      </Popover>
                    )}
                </div>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <Card type={"simple"}>
      <Table
        dataSource={data}
        columns={columns}
        className="table__wrapper"
        rowKey={"id"}
        pagination={false}
        scroll={{ x: true }}
        tableLayout="auto"
      />
      <Pagination
        key={paginationFootprint}
        footprint={paginationFootprint}
        count={count}
        setValues={setValues}
        skip={skip || 0}
        take={pagination.take}
      />
    </Card>
  );
};

export default ProjectsTable;
